import cls from 'classnames';
import { FC, memo } from 'react';

import { AdSlot } from '@/types/views/generic';

import Advertisement from '../..';
import styles from './styles';

interface CommonAdBoxProps {
  className?: string;
  isCenter?: boolean;
}

const CommonAdBox: FC<CommonAdBoxProps> = ({
  children,
  className = '',
  isCenter = true,
}) => (
  <aside
    className={cls(styles.commonAdBox, className, isCenter && 'justify-center')}
    style={{ padding: 0 }}
  >
    {/* TODO: Probably could use Advertisement component here instead, refactor */}
    {children}
  </aside>
);

interface WideBoardAdProps {
  position?: 1 | 2 | 3;
  targetingPos?: number;
  deactivateAdSlots?: boolean;
}

const WideBoardAd: FC<WideBoardAdProps> = ({
  position = 1,
  targetingPos,
  deactivateAdSlots,
}) => {
  if (deactivateAdSlots) return null;
  let slotName: AdSlot;
  let slotId: string;
  switch (position) {
    case 1:
      slotName = 'top_ad_1';
      slotId = 'ad-slot-top_ad_1';
      break;
    case 2:
      slotName = 'index_ad_1';
      slotId = 'ad-slot-index_ad_1';
      break;
    case 3:
      slotName = 'bottom_ad_1';
      slotId = 'ad-slot-bottom_ad_1';
      break;
  }

  return (
    <CommonAdBox>
      <Advertisement
        slot={slotName}
        {...(targetingPos && { slotId: `${slotId}-${targetingPos}` })}
      />
    </CommonAdBox>
  );
};

interface SidebarAdProps {
  position?: 'left' | 'right';
  slotType: 'right_ad_1' | 'left_ad_1';
}

export const SidebarAd = ({ position = 'right', slotType }: SidebarAdProps) => (
  // TODO: Needs to adjust back to top-28 on scroll position other than 0
  <div className={styles.sideAdTopContainer(position)}>
    <div className={styles.sideAdBottomContainer(position)}>
      {/* TODO: Might have to remove constant w-full h-full so Ads.helper methods work as expected */}
      <div
        id={`ads-papi-sidebar-${position}`}
        className={cls(
          'ad-sidebar-slot h-full',
          position === 'left' ? 'ad-sidebar--left' : 'ad-sidebar--right',
        )}
      >
        <Advertisement slot={slotType} />
      </div>
    </div>
  </div>
);

type MobileMediumRectangleAdProps = {
  position: 1 | 2;
  targetingPos?: number;
  isWatchExperienceAd?: boolean;
  deactivateAdSlots?: boolean;
};

interface MobileHalfPageAdProps {
  targetingPos?: number;
  slot: 'index_ad_1' | 'bottom_ad_1';
  deactivateAdSlots?: boolean;
}

const getMobileHalfPageSlotId = (
  slot: MobileHalfPageAdProps['slot'],
  targetingPos?: number,
) => {
  if (slot === 'bottom_ad_1') {
    return targetingPos
      ? `ad-slot-bottom_ad_1-mhpa-${targetingPos}`
      : 'ad-slot-bottom_ad_1-mhpa';
  }

  return targetingPos ? `ad-slot-index_ad_1-mhpa-${targetingPos}` : undefined;
};

const MobileHalfPageAd: FC<MobileHalfPageAdProps> = ({
  targetingPos,
  slot,
  deactivateAdSlots,
}) => {
  if (deactivateAdSlots) return null;

  return (
    <CommonAdBox>
      <Advertisement
        slot={slot}
        additionalContainerClass="ad-mobile-half-page"
        slotId={getMobileHalfPageSlotId(slot, targetingPos)}
      />
    </CommonAdBox>
  );
};

type MediumRectangleAdProps = WideBoardAdProps & {
  className?: string;
  isWatchExperienceAd?: boolean;
};

interface WrapperComponent {
  children: JSX.Element;
}

const MediumRectangleAd: FC<MediumRectangleAdProps> = memo(
  ({
    position = 1,
    targetingPos,
    className = '',
    isWatchExperienceAd = false,
    deactivateAdSlots,
  }) => {
    if (deactivateAdSlots) return null;

    const WrapperComponent: FC<WrapperComponent> = ({ children }) =>
      isWatchExperienceAd ? (
        <div
          className={cls(
            'aspect aspect-3-4 w-full bg-black-light rounded-4',
            'md:aspect-3-2 xl:aspect-3-4 2xl:aspect-3-2',
          )}
        >
          <div className="flex items-center justify-center">{children}</div>
        </div>
      ) : (
        children
      );

    return (
      <CommonAdBox
        className={cls(
          'flex flex-col justify-start',
          isWatchExperienceAd && 'md:col-span-2 xl:col-span-1 2xl:col-span-2',
          className,
        )}
        isCenter={false}
      >
        <WrapperComponent>
          <Advertisement
            slot="index_ad_2"
            {...(targetingPos && {
              slotId: `ad-slot-index_ad_2-${targetingPos}`,
            })}
          />
        </WrapperComponent>

        {isWatchExperienceAd && (
          <div className="text-gray-2 text-overline mt-2">Werbung</div>
        )}
      </CommonAdBox>
    );
  },
);

const MobileMediumRectangleAd: FC<MobileMediumRectangleAdProps> = memo(
  ({
    position = 1,
    targetingPos,
    isWatchExperienceAd = false,
    deactivateAdSlots,
  }) => {
    if (deactivateAdSlots) return null;

    return (
      <CommonAdBox
        className={cls(isWatchExperienceAd && 'col-span-2 -mx-[1.25rem]')}
      >
        <Advertisement
          slot={position === 1 ? 'top_ad_1' : 'index_ad_1'}
          {...(targetingPos && {
            slotId:
              position === 1
                ? 'ad-slot-top_ad_1'
                : `ad-slot-index_ad_1-${targetingPos}`,
          })}
        />
      </CommonAdBox>
    );
  },
);

const SectionAds = {
  WideBoardAd,
  SidebarAd,
  MobileMediumRectangleAd,
  MobileHalfPageAd,
  MediumRectangleAd,
};

export default SectionAds;
